import { api } from './api'
import type {
  RackConfig,
  LayoutAnalysis,
  RackLayoutUpdateItem,
  AgentEvent,
  AiTool,
} from '../types'

// ---------------------------------------------------------------------------
// Rack layout REST helpers
// ---------------------------------------------------------------------------
export async function fetchRacks(code: string, workspace: string = 'master'): Promise<RackConfig[]> {
  const { data } = await api.get(`/api/datacenters/${code}/racks`, {
    params: { workspace }
  })
  return data
}

export async function fetchLayoutAnalysis(code: string): Promise<LayoutAnalysis> {
  const { data } = await api.get(`/api/datacenters/${code}/layout/analyze`)
  return data
}

export async function saveRackLayout(
  code: string,
  racks: RackLayoutUpdateItem[],
): Promise<{ updated: RackLayoutUpdateItem[]; count: number }> {
  const { data } = await api.patch(
    `/api/datacenters/${code}/racks/layout`,
    { racks },
    // The backend enforces CSRF on unsafe methods via this header.
    { headers: { 'X-Requested-With': 'XMLHttpRequest' } },
  )
  return data
}

export async function fetchAiTools(code: string): Promise<AiTool[]> {
  const { data } = await api.get(`/api/datacenters/${code}/ai/tools`)
  return data.tools ?? []
}

// ---------------------------------------------------------------------------
// AI agent — Server-Sent Events streaming
// ---------------------------------------------------------------------------
// We use fetch (not axios) because axios does not expose a streaming body in
// the browser. Cookies are sent with credentials: 'include'.
export async function streamAiAgent(
  code: string,
  prompt: string,
  opts: {
    allowMutations?: boolean
    signal?: AbortSignal
    onEvent: (event: AgentEvent) => void
  },
): Promise<void> {
  const resp = await fetch(`/api/datacenters/${code}/ai/agent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
    signal: opts.signal,
    body: JSON.stringify({ prompt, allow_mutations: opts.allowMutations ?? false }),
  })

  if (!resp.ok || !resp.body) {
    const detail = await resp.text().catch(() => resp.statusText)
    throw new Error(`AI agent request failed (${resp.status}): ${detail}`)
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Parse the SSE stream: events are separated by a blank line, payload lines
  // are prefixed with "data:".
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    while (true) {
      let sep = buffer.indexOf('\n\n')
      let sepLen = 2
      if (sep === -1) {
        sep = buffer.indexOf('\r\n\r\n')
        sepLen = 4
      }
      if (sep === -1) break

      const chunk = buffer.slice(0, sep)
      buffer = buffer.slice(sep + sepLen)

      const dataLine = chunk
        .split('\n')
        .find((l) => l.startsWith('data:'))
      if (!dataLine) continue

      const json = dataLine.slice('data:'.length).trim()
      if (!json) continue
      try {
        opts.onEvent(JSON.parse(json) as AgentEvent)
      } catch {
        // Ignore malformed keep-alive or partial frames.
      }
    }
  }
}

export async function streamWorkOrderAiAgent(
  code: string,
  woId: string,
  prompt: string,
  history: any[] = [],
  opts: {
    signal?: AbortSignal
    onEvent: (event: AgentEvent) => void
  }
): Promise<void> {
  const resp = await fetch(`/api/datacenters/${code}/impact-analysis/work-orders/${woId}/ai-agent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
    signal: opts.signal,
    body: JSON.stringify({ prompt, history }),
  })

  if (!resp.ok || !resp.body) {
    const detail = await resp.text().catch(() => resp.statusText)
    throw new Error(`AI agent request failed (${resp.status}): ${detail}`)
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    while (true) {
      let sep = buffer.indexOf('\n\n')
      let sepLen = 2
      if (sep === -1) {
        sep = buffer.indexOf('\r\n\r\n')
        sepLen = 4
      }
      if (sep === -1) break

      const chunk = buffer.slice(0, sep)
      buffer = buffer.slice(sep + sepLen)

      const dataLine = chunk
        .split('\n')
        .find((l) => l.startsWith('data:'))
      if (!dataLine) continue

      const json = dataLine.slice('data:'.length).trim()
      if (!json) continue
      try {
        opts.onEvent(JSON.parse(json) as AgentEvent)
      } catch {
        // Ignore malformed keep-alive or partial frames.
      }
    }
  }
}
